import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Container,
    Grid,
    Paper,
    Typography,
    Avatar,
    useTheme,
    Skeleton,
    IconButton,
    Button,
    Tooltip,
    CircularProgress,
    Stack,
} from "@mui/material";
import {
    CalendarMonth,
    LiveTv,
    LanguageOutlined,
    LocationOn,
    Movie,
    Update,
    PlayArrow,
    Diversity2,
    Hub,
    MonetizationOn,
    AssuredWorkload,
    ThumbUpRounded,
    SmsRounded,
    MoreHoriz,
    Share,
    Reply,
    Comment,
    ArrowBack,


} from "@mui/icons-material";
import { getImageUrlWithToken } from "../../../utils/ImageUtils";
import { actionOnProject, getProjectById } from "../../services/Project";
import { formatBudgetAmount } from "../../../utils/formatBudgetAmount";
import currencyOption from "../../../data/currencies.json";
import { UserContext } from "../../../context";
import TeamPublicView from "../../../components/projects/TeamPublicView";
import TrailerGrid from "../../../components/projects/TrailerGrid";
import ReadMoreText from "../../../utils/ReadMoretext";
import { getImageUrlAdminToken } from "../../utills/ImagesUtilsAdmin";
// import ProjectComment from "../../../components/projects/ProjectComment";

const ProjectDetails = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { projectId } = useParams();
    const [projectDetails, setProjectDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user } = useContext(UserContext);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };


    // Fetch project details by ID
    const fetchProjectDetails = async () => {
        try {
            setLoading(true);
            const response = await getProjectById(projectId);
            console.log("Project Details Response:", response);
            setProjectDetails(response.data[0]);
        } catch (error) {
            console.error("Error fetching project details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjectDetails();
    }, [projectId]);


    const handleApprove = async (projectId, status) => {
        try {
            const response = await actionOnProject(projectId, status);
            console.log("Project Approval Response:", response);
            fetchProjectDetails();

        } catch (error) {
            console.error("Error approving project:", error);
        }

    };


    const handleRestrict = async (projectId, status) => {
        try {
            const response = await actionOnProject(projectId, status);
            console.log("Project Restriction Response:", response);
            fetchProjectDetails();

        } catch (error) {
            console.error("Error restricting project:", error);
        }
    }



    return (
        <>
            {loading ? (
                <Box sx={{ textAlign: "center", mt: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                        Loading ...
                    </Typography>
                </Box>
            ) : (
                <>
                    <Container maxWidth="lg" sx={{ px: 0.5, mt: 1 }}>
                        <Stack direction="row" alignItems="center" mb={1} px={{ xs: 0.5, sm: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                <IconButton sx={{ mr: 1 }} onClick={() => navigate(-1)}>
                                    <ArrowBack />
                                </IconButton>
                                <Typography variant="h6">
                                    All Projects
                                </Typography>
                            </Box>

                            <>
                                <Tooltip title=" Approve Project" arrow placement="top">
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        color="primary"
                                        sx={{
                                            textTransform: 'none',
                                            padding: '6px 30px',
                                        }}
                                        onClick={() => handleApprove(projectDetails?.project_id, "Approved")}

                                        disabled={projectDetails?.admin_project_status === "Approved"}
                                    >
                                        Approve
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Restrict Project" arrow placement="top">
                                    <Button
                                        variant="outlined"
                                        disableElevation
                                        color="error"
                                        sx={{
                                            textTransform: 'none',
                                            padding: '6px 30px',
                                            ml: 1,
                                        }}
                                        onClick={() => handleRestrict(projectDetails?.project_id, "Restricted")}
                                        disabled={projectDetails?.admin_project_status === "Restricted"}
                                    >
                                        Restrict
                                    </Button>
                                </Tooltip>
                            </>

                        </Stack>
                        <Paper elevation={0}>
                            {/* Main Content */}
                            <Grid
                                container
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Card elevation={0} sx={{ width: "100%" }}>
                                    <Box sx={{ position: "relative" }}>
                                        {!projectDetails ? (
                                            <Skeleton
                                                variant="rectangular"
                                                width="100%"
                                                height={300}
                                                animation="wave"
                                                sx={{ borderRadius: 0 }}
                                            />
                                        ) : (
                                            <CardMedia
                                                component="img"
                                                height={300}
                                                image={getImageUrlWithToken(projectDetails?.poster_image)}
                                                alt={`${projectDetails?.title} Poster`}
                                                sx={{ objectFit: "cover", objectPosition: "top" }}
                                            />
                                        )}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                background: theme.palette.custom.gradient,
                                                pointerEvents: "none",
                                            }}
                                        />
                                    </Box>

                                    <CardContent>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                                mb: 2,
                                            }}
                                        >


                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", }}>
                                                <Typography
                                                    variant="h5"
                                                    sx={{ fontWeight: "bold", textTransform: "capitalize" }}
                                                >
                                                    {projectDetails?.title || "Project Title"}
                                                </Typography>

                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Tooltip title="Project Status By Admin" arrow placement="top">
                                                        <Chip
                                                            variant="contained"
                                                            color='primary'
                                                            label={projectDetails?.admin_project_status}
                                                            size="small"
                                                            onClick={() => { }}
                                                            clickable={false}
                                                            sx={{
                                                                textTransform: 'capitalize',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: { xs: "flex-start", sm: "center" },
                                                    flexDirection: { xs: "column", sm: "row" },
                                                    gap: 1,
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Avatar
                                                        src={getImageUrlAdminToken(
                                                            projectDetails.user.thumbnail_pic
                                                        )}
                                                        alt={projectDetails?.user?.name || "Creator Name"}
                                                        sx={{ width: 40, height: 40, cursor: 'pointer' }}
                                                        onClick={() => {
                                                            navigate(`/users/${projectDetails.user_id}`);
                                                        }}
                                                    />
                                                    <Typography variant="h6">
                                                        {projectDetails?.user?.name || "Creator Name"}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", }}>
                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        sx={{ textTransform: "none", }}
                                                        startIcon={<ThumbUpRounded />}
                                                        disabled
                                                    >
                                                        {projectDetails?.people_action_data?.like_counts || 0}
                                                    </Button>

                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        sx={{ textTransformation: "none" }}
                                                        startIcon={<Comment />}
                                                        disabled
                                                    >
                                                        {projectDetails?.people_action_data.comment_counts || 0}
                                                    </Button>

                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        sx={{ textTransform: "none" }}
                                                        startIcon={<Reply sx={{ transform: "scaleX(-1)" }} />}
                                                        disabled
                                                    // onClick={() => handleShareProject(projectDetails?.project_id)}
                                                    >
                                                        {projectDetails?.people_action_data.share_counts || 0}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                                            <ReadMoreText text={projectDetails?.synopsis} maxLength={500} />
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                                            {projectDetails?.work_project_type?.map((type) => (
                                                <Chip clickable={false}
                                                    onClick={() => { }}
                                                    key={type.work_project_type_id}
                                                    label={type.project_type_name}
                                                    color="primary"
                                                    size="small"
                                                />
                                            ))}
                                            {projectDetails?.project_status && (
                                                <Chip clickable={false}
                                                    onClick={() => { }}
                                                    size="small"
                                                    label={projectDetails.project_status}
                                                    sx={{
                                                        backgroundColor: theme.palette.background.default,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>

                                {/* Project Information, Budget & Timeline, Production & Distribution */}
                                <Grid container spacing={3} sx={{ m: 2 }}>
                                    {/* Project Information */}
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Card sx={{ height: "100%" }}>
                                            <CardContent>
                                                <Typography
                                                    variant="h6"
                                                    sx={{ mb: 2, display: "flex", alignItems: "center" }}
                                                >
                                                    <LiveTv sx={{ mr: 1 }} />
                                                    Project Information
                                                </Typography>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                    >
                                                        <LanguageOutlined sx={{ mr: 1 }} fontSize="small" />
                                                        Languages:
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        {projectDetails?.languages && projectDetails.languages.length > 0 ? (
                                                            projectDetails.languages.map((lang) => (
                                                                <Chip clickable={false}
                                                                    onClick={() => { }}
                                                                    key={lang.id}
                                                                    label={lang.language}
                                                                    size="small"
                                                                />
                                                            ))
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Not specified
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                    >
                                                        <PlayArrow sx={{ mr: 1 }} fontSize="small" />
                                                        Target Platforms:
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        {projectDetails?.target_platforms && projectDetails.target_platforms.length > 0 ? (
                                                            projectDetails.target_platforms.map(
                                                                (platform, index) => (
                                                                    <Chip clickable={false}
                                                                        onClick={() => { }} key={index} label={platform} size="small" />
                                                                )
                                                            )
                                                        ) : (
                                                            <Typography variant="body2" >
                                                                Not specified
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                    >
                                                        <LocationOn sx={{ mr: 1 }} fontSize="small" />
                                                        Filming Locations:
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        {projectDetails?.locations.map((loc, index) => (
                                                            <Chip clickable={false}
                                                                onClick={() => { }}
                                                                key={index}
                                                                label={`${loc.city}, ${loc.country}`}
                                                                size="small"
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Budget & Timeline Genres */}
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Card sx={{ height: "100%" }}>
                                            <CardContent>
                                                <Typography
                                                    variant="h6"
                                                    sx={{ mb: 2, display: "flex", alignItems: "center" }}
                                                >
                                                    <Movie sx={{ mr: 1 }} />
                                                    Budget & Timeline Genres
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                                    <AssuredWorkload sx={{ mr: 1 }} fontSize="small" />
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Budget:
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {formatBudgetAmount(
                                                            projectDetails?.budget,
                                                            currencyOption
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                                    <CalendarMonth sx={{ mr: 1 }} fontSize="small" />
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Start Date:
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {formatDate(projectDetails?.start_date)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                                    <CalendarMonth sx={{ mr: 1 }} fontSize="small" />
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        End Date:
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {formatDate(projectDetails?.end_date || "N/A")}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                                    <Update sx={{ mr: 1 }} fontSize="small" />
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Last Updated:
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {formatDate(projectDetails?.updated_at)}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                    >
                                                        <PlayArrow sx={{ mr: 1 }} fontSize="small" />
                                                        Genres:
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        {projectDetails?.genres?.map((genre, index) => (
                                                            <Chip clickable={false}
                                                                onClick={() => { }}
                                                                key={genre.id || index}
                                                                label={genre.name}
                                                                size="small"
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Production & Distribution */}
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Card sx={{ height: "100%" }}>
                                            <CardContent>
                                                <Typography
                                                    variant="h6"
                                                    sx={{ mb: 2, display: "flex", alignItems: "center" }}
                                                >
                                                    <LiveTv sx={{ mr: 1 }} />
                                                    Production & Distribution
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <Diversity2 sx={{ mr: 1 }} fontSize="small" />
                                                    Producer
                                                </Typography>
                                                <Box
                                                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}
                                                >
                                                    {projectDetails?.producer && projectDetails.producer.length > 0 ? (
                                                        projectDetails?.producer?.map((name, index) => (
                                                            <Chip clickable={false}
                                                                onClick={() => { }}
                                                                key={index}
                                                                label={name}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" >
                                                            Not specified
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <Hub sx={{ mr: 1 }} fontSize="small" />
                                                    Distributor
                                                </Typography>
                                                <Box
                                                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}
                                                >
                                                    {projectDetails?.distributor && projectDetails.distributor.length > 0 ? (
                                                        projectDetails.distributor.map((name, index) => (
                                                            <Chip clickable={false}
                                                                onClick={() => { }}
                                                                key={index}
                                                                label={name}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" >
                                                            Not specified
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                                >
                                                    <MonetizationOn sx={{ mr: 1 }} fontSize="small" />
                                                    Investors
                                                </Typography>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    {projectDetails?.investor && projectDetails.investor.length > 0 ? (
                                                        projectDetails?.investor?.map((name, index) => (
                                                            <Chip clickable={false}
                                                                onClick={() => { }}
                                                                key={index}
                                                                label={name}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" >
                                                            Not specified
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {projectDetails?.pid_required && (
                                        <Grid size={{ xs: 12 }}>
                                            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                                                <Stack
                                                    direction="row"
                                                    sx={{
                                                        textAlign: "center",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: 2,
                                                        flexWrap: "wrap"
                                                    }}
                                                >
                                                    <Typography variant="h5" component="div">
                                                        <span dangerouslySetInnerHTML={{ __html: projectDetails?.pid_required }}></span>
                                                    </Typography>
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    )}

                                    {/* Trailer Preview */}
                                    <Grid size={{ xs: 12 }}>
                                        <TrailerGrid
                                            trailerLinks={projectDetails?.trailer_link}
                                        />
                                    </Grid>

                                    {/* Team Members */}
                                    <Grid size={{ xs: 12 }}>
                                        <TeamPublicView
                                            sectionTitle="Team Members"
                                            sectionData={projectDetails?.team_members || []}
                                            isCreator={user && projectDetails?.user?.id === user.id}
                                            projectId={projectDetails?.project_id}
                                        />
                                    </Grid>

                                    {/* Comment Section */}
                                    {/* <Grid size={{ xs: 12 }}>
                                        <ProjectComment
                                            postId={projectDetails?.project_id}
                                            user={user}
                                            projectUserId={projectDetails?.user?.id}
                                        />
                                    </Grid> */}

                                </Grid>
                            </Grid>
                        </Paper>
                    </Container>
                </>
            )}
        </>
    );
};

export default ProjectDetails;